import { useRef, useCallback, type CSSProperties } from 'react';

type SwipeDirection = 'left' | 'right';

interface UseSwipeToCloseOptions {
  direction: SwipeDirection;
  onClose: () => void;
  threshold?: number; // px to trigger close (default: 80)
}

interface SwipeHandlers {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
}

interface UseSwipeToCloseReturn {
  handlers: SwipeHandlers;
  getStyle: () => CSSProperties;
}

/**
 * Hook for swipe-to-close gesture on mobile drawers.
 * Left drawer: swipe left to close. Right drawer: swipe right to close.
 * Uses refs for drag state to avoid re-renders during gesture.
 * Calls getStyle() in a rAF-driven way for smooth animation.
 */
export function useSwipeToClose({
  direction,
  onClose,
  threshold = 80,
}: UseSwipeToCloseOptions): UseSwipeToCloseReturn {
  const startX = useRef(0);
  const startY = useRef(0);
  const currentX = useRef(0);
  const isDragging = useRef(false);
  const isHorizontal = useRef<boolean | null>(null); // null = not yet determined
  const drawerRef = useRef<HTMLElement | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    startX.current = touch.clientX;
    startY.current = touch.clientY;
    currentX.current = touch.clientX;
    isDragging.current = false;
    isHorizontal.current = null;
    // Store ref to the drawer element for direct DOM updates
    drawerRef.current = e.currentTarget as HTMLElement;
    // Disable transition during drag for instant feedback
    if (drawerRef.current) {
      drawerRef.current.style.transition = 'none';
    }
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const deltaX = touch.clientX - startX.current;
    const deltaY = touch.clientY - startY.current;

    // Determine scroll direction on first significant move
    if (isHorizontal.current === null) {
      const absDx = Math.abs(deltaX);
      const absDy = Math.abs(deltaY);
      if (absDx > 5 || absDy > 5) {
        isHorizontal.current = absDx > absDy;
      }
      if (!isHorizontal.current) return;
    }

    // Only track horizontal swipes
    if (!isHorizontal.current) return;

    // For left drawer: only allow negative deltaX (swipe left)
    // For right drawer: only allow positive deltaX (swipe right)
    const validSwipe = direction === 'left' ? deltaX < 0 : deltaX > 0;
    if (!validSwipe) {
      // Reset if swiping wrong direction
      if (drawerRef.current) {
        drawerRef.current.style.transform = '';
      }
      return;
    }

    isDragging.current = true;
    currentX.current = touch.clientX;

    // Apply transform directly to DOM for performance
    const translate = direction === 'left'
      ? Math.min(0, deltaX)  // Negative = moving left
      : Math.max(0, deltaX); // Positive = moving right

    if (drawerRef.current) {
      drawerRef.current.style.transform = `translateX(${translate}px)`;
    }
  }, [direction]);

  const onTouchEnd = useCallback(() => {
    // Re-enable transitions
    if (drawerRef.current) {
      drawerRef.current.style.transition = '';
    }

    if (!isDragging.current) {
      if (drawerRef.current) {
        drawerRef.current.style.transform = '';
      }
      return;
    }

    const deltaX = currentX.current - startX.current;
    const absDelta = Math.abs(deltaX);

    if (absDelta > threshold) {
      // Swipe distance exceeded threshold - close the drawer
      // Animate out before calling onClose
      if (drawerRef.current) {
        const translateOut = direction === 'left' ? '-100%' : '100%';
        drawerRef.current.style.transition = 'transform 0.15s ease-out';
        drawerRef.current.style.transform = `translateX(${translateOut})`;
      }
      // Wait for animation then close
      setTimeout(onClose, 150);
    } else {
      // Snap back
      if (drawerRef.current) {
        drawerRef.current.style.transition = 'transform 0.2s ease-out';
        drawerRef.current.style.transform = 'translateX(0)';
      }
    }

    isDragging.current = false;
    isHorizontal.current = null;
  }, [direction, threshold, onClose]);

  // Style getter (fallback, prefer direct DOM manipulation above)
  const getStyle = useCallback((): CSSProperties => ({}), []);

  return {
    handlers: { onTouchStart, onTouchMove, onTouchEnd },
    getStyle,
  };
}
